import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { RootErrorBoundary, RouteErrorBoundary, InstallPrompt, DialogProvider, CookieConsent, Footer, PrivacyPolicy, TermsOfService } from "@bc/ui";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import VerifyEmailScreen from "@/components/VerifyEmailScreen";
import "@/App.css";

const AuthPage = lazy(() => import("./pages/AuthPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Profile = lazy(() => import("./pages/Profile"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-darkblue" />
    </div>
  );
}

// The seller portal scrolls the document body — reset it on navigation so
// every page opens at the top (mirrors the buyer portal's PublicShell).
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

// Mobile browsers aggressively restore scroll positions from history on
// back/forward navigation, which can override the per-route scroll resets.
// Take manual control so every page reliably opens at the top.
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

function withRouteBoundary(element) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
    </RouteErrorBoundary>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (user.is_verified === false) return <VerifyEmailScreen user={user} />;
  return children;
}

function App() {
  return (
    <RootErrorBoundary>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
          <DialogProvider>
            <Routes>
              <Route path="/" element={withRouteBoundary(<AuthPage />)} />
              <Route
                path="/dashboard"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/profile"
                element={withRouteBoundary(
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                )}
              />
              <Route path="/verify-email" element={withRouteBoundary(<VerifyEmail />)} />
              <Route path="/forgot-password" element={withRouteBoundary(<ForgotPassword />)} />
              <Route path="/reset-password" element={withRouteBoundary(<ResetPassword />)} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Footer />
            <CookieConsent />
            <InstallPrompt />
          </DialogProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </RootErrorBoundary>
  );
}

export default App;
